import { Button, Input, Modal, Select, Space, DatePicker, message, Tabs, Upload, Row, Col, Spin, Form, Pagination, Checkbox } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { orderColumns } from './constants';
import "./dispatch.scss";
import { SearchOutlined } from '@ant-design/icons';
import { getUserDetails, makePostCall } from '../../utils/helper';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';

import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import AgGridTable from '../../components/AgGridTable';
import { debounce } from 'lodash';
import ReportViewer from '../ReportViewer';
import { useForm } from 'antd/es/form/Form';
import { launchViewerOnDualMonitor, checkMonitorSetup } from '../../utils/MonitorManager';
import { FieldLabels } from '../../constants/fieldLabels';

const { RangePicker } = DatePicker;

const DispatchList = ({ isConsultant, StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState({});
  // const [savedFilters, setSavedFilters] = useState([]);
  const userDetails = getUserDetails();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [viewreportModal, setViewReportModal] = useState({ visible: false, pdfData: null });
  const [receiverModal, setReceiverModal] = useState({ visible: false, details: null });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const [receiverForm] = useForm();

  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');

  // Set the default value to [yesterday, today]
  const [dateRange, setDateRange] = useState([yesterday, today]);
  const [printLoading, setPrintLoading] = useState(false);
  // Modality multi-select (matches radiologist/technician pages)
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const { modalityOptions } = useAllowedModalities();

  const openReport = async record => {
    if (isConsultant) {
      // window.open(`/viewer?StudyInstanceUIDs=${record?.po_study_uid}`, '_blank');
      await launchViewerOnDualMonitor(record?.po_study_uid, record);
    }
    // printReport(record)
    captureReceiver(record);
  };

  const filterResults = (tempFilters, page = 1, pageSize = 20, overrideDateRange = undefined) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };

    const effectiveDateRange = overrideDateRange !== undefined ? overrideDateRange : dateRange;
    if (effectiveDateRange) {
      payload['from_date'] = dayjs(effectiveDateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(effectiveDateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = effectiveDateRange;
    }

    makePostCall('/dispatch-list', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 });
      });
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  };

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setSelectedRowKeys([]);
    setModalityAllSelected(true);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize, null);
  };

  useEffect(() => {
    console.log('Filters changed', filters);
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log('date changed', filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce(tempFilters => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const statusOptions =
    userDetails?.user_type === 'doc'
      ? [{ label: 'SIGNEDOFF', value: 'SIGNEDOFF' }]
      : [
          { label: 'NEW', value: 'PENDING' },
          { label: 'SCANNED', value: 'SCANNED' },
          { label: 'DRAFTED', value: 'DRAFTED' },
          { label: 'REVIEWED', value: 'REVIEWED' },
          { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
        ];

  const bodyPartOptions = [
    { label: 'Head', value: 'HEAD' },
    { label: 'Brain', value: 'BRAIN' },
    { label: 'Abdomen', value: 'ABDOMEN' },
    { label: 'Chest', value: 'CHEST' },
    { label: 'Leg', value: 'LEG' },
    { label: 'Hand', value: 'HAND' },
  ];

  const siteOptions = [
    { label: 'Somajiguda', value: 'SOMAJIGUDA' },
    { label: 'HiTech City', value: 'HITEC CITY' },
    { label: 'Secunderabad', value: 'SECUNDERABAD' },
  ];

  const captureReceiver = rec => {
    setReceiverModal({ visible: true, details: rec });
  };

  const captureAndPrint = () => {
    const receiverInfo = receiverForm.getFieldsValue();
    printReport(receiverModal?.details, receiverInfo);
  };

  const printReport = async (rec, receiverInfo) => {
    // A dispatch-list row IS the order (pacs_ord_id at top level); older callers
    // may pass a nested { pacs_order } shape, so fall back to that.
    const orderId = rec?.pacs_ord_id ?? rec?.pacs_order?.pacs_ord_id;
    const { receiver_mobile, receiver_name } = receiverInfo || {};
    setReceiverModal({ visible: false });
    setPrintLoading(true);
    makePostCall(
      '/print-acc-report',
      {
        order_id: orderId,
        user_id: userDetails?.username,
        fromDispatch: !isConsultant || userDetails?.user_type === 'dispatch',
        received_by: receiverInfo ? `${receiver_name} | ${receiver_mobile}` : null,
      },
      {
        responseType: 'arraybuffer',
      }
    )
      .then(async res => {
        const pdfBlob = new Blob([res.data], { type: 'application/pdf' });

        const pdf_url = URL.createObjectURL(pdfBlob);

        setPrintLoading(false);
        const buffer = await pdfBlob.arrayBuffer();

        if (isConsultant) {
          setViewReportModal({ visible: true, pdfData: buffer, pdfBlob: pdfBlob, pdf_url });
        } else {
          // setPdfUrl(url);
          const printWindow = window.open(pdf_url, '_blank');
          printWindow.print();
        }
      })
      .catch(err => {
        console.log('Error', err);
        setPrintLoading(false);
      });
  };

  return (
    <Spin spinning={printLoading}>
      <div>
        {StyledTabsComponent && StyledTabsComponent()}
        <div style={{ padding: '0.75rem 1.25rem 0' }}>
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '0.75rem',
              border: '1px solid #E2E8F1',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
            }}
          >
            {/* Filters: 70% inputs (action buttons inline beside date) | 30% modality */}
            <div className="filter-layout">
              <div className="disp-filters-row filter-layout-inputs">
                <div className="disp-field disp-field--name">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_NAME}
                    value={filters['pat_name'] || ''}
                    onChange={e => handleFilterChange('pat_name', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--id">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_ID_SHORT}
                    value={filters['po_pin'] || ''}
                    onChange={e => handleFilterChange('po_pin', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--acc">
                  <FyzksInput
                    size="small"
                    placeholder="Acc. No"
                    value={filters['po_acc_no'] || ''}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--ref">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.REFERRING_DOCTOR}
                    value={filters['po_ref_doc'] || ''}
                    onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--body">
                  <FyzksInput
                    size="small"
                    placeholder="Body Part / Study Desc"
                    value={filters['po_body_part'] || ''}
                    onChange={e => handleFilterChange('po_body_part', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--date">
                  <RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    placeholder={['Study Date', '']}
                    value={dateRange}
                    onChange={val => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </div>

                <div className="disp-actions">
                  <button className="btn-secondary btn-compact" onClick={clearFilters}>
                    Clear Search
                  </button>
                  <button className="btn-primary btn-compact" onClick={() => filterResults(filters)}>
                    <SearchOutlined /> Search
                  </button>
                </div>
              </div>

              <div className="filter-layout-modalities">
                <div className="filter-modalities-header">
                  <span className="filter-modalities-label">Modality</span>
                  <Checkbox
                    className="filter-modalities-selectall"
                    checked={modalityAllSelected}
                    indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                    onChange={e => {
                      setModalityAllSelected(e.target.checked);
                      handleFilterChange('modality', []);
                    }}
                  >
                    Select all
                  </Checkbox>
                </div>
                <Checkbox.Group
                  value={modalityAllSelected ? modalityOptions.map(o => o.value) : (filters['modality'] || [])}
                  onChange={vals => {
                    const allChecked = vals.length === modalityOptions.length;
                    setModalityAllSelected(allChecked);
                    handleFilterChange('modality', allChecked ? [] : vals);
                  }}
                >
                  {modalityOptions.map(opt => (
                    <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </div>
          </div>
          <div className="orders-list">
            <AgGridTable
              height="calc(100vh - 18rem)"
              loading={orders.loading}
              rowData={orders.data || []}
              rowKey={(rec) => rec?.po_acc_no ?? rec?.pacs_order?.pacs_ord_id}
              columns={orderColumns({
                openReportEditor: openReport,
                role: userDetails?.user_type,
                printReport,
                captureReceiver,
              })}
            />
            {/* External pagination for server-side paging */}
            <div className="pacs-ag-grid-pagination">
              <Pagination
                size="small"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={orders?.total || 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination({ current: page, pageSize });
                  filterResults(filters, page, pageSize);
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          </div>
          {viewreportModal && viewreportModal?.visible && (
            <Modal
              open={viewreportModal.visible}
              onCancel={() => {
                setViewReportModal({ visible: false, pdfData: null });
              }}
              width={900}
              className="viewer-modal"
            >
              <ReportViewer
                pdfData={viewreportModal?.pdfData}
                pdfBlob={viewreportModal?.pdfBlob}
                pdf_url={viewreportModal?.pdf_url}
              />
            </Modal>
          )}
          {receiverModal && receiverModal.visible && (
            <Modal
              open={receiverModal.visible}
              onCancel={() => {
                setReceiverModal(null);
              }}
              onOk={() => {
                setReceiverModal(null);
              }}
              width={'37.5rem'}
              okButtonProps={{ style: { display: 'none' } }}
              className="receiver-modal"
            >
              <div style={{ padding: '1rem 0.5rem' }}>
                <Form
                  form={receiverForm}
                  onFinish={() => {
                    captureAndPrint();
                  }}
                >
                  <Form.Item
                    name="receiver_name"
                    label="Receiver Name"
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="receiver_mobile"
                    label="Receiver Mobile"
                    rules={[
                      {
                        pattern: /^[6-9]\d{9}$/,
                        message: 'Please enter a valid 10-digit mobile number!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item className="text-center">
                    <div>
                      <Button
                        type="primary"
                        htmlType="submit"
                        style={{ width: '18.75rem' }}
                      >
                        Proceed
                      </Button>
                    </div>
                    <div>
                      <Button
                        type="link"
                        onClick={() => printReport(receiverModal?.details)}
                      >
                        Continue without Info
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default DispatchList;
